import { Certificate } from './Certificate';
import { Photo, PrintSize } from '../types';
import { X } from 'lucide-react@0.487.0';
import { useRef, useState, useEffect, useCallback } from 'react';

interface CertificatePreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photo: Photo;
  size: PrintSize;
  editionNumber: number;
}

// Certificate is 210mm × 297mm. At 96dpi that's ~794 × 1123 px.
const CERT_W = 794;
const CERT_H = 1123;

export function CertificatePreview({ open, onOpenChange, photo, size, editionNumber }: CertificatePreviewProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  const computeScale = useCallback(() => {
    if (!wrapRef.current) return;
    const maxW = wrapRef.current.clientWidth;
    const maxH = window.innerHeight * 0.72;
    const s = Math.min(maxW / CERT_W, maxH / CERT_H, 0.7);
    setScale(s);
  }, []);

  useEffect(() => {
    if (!open) return;
    // Wait a tick for the DOM to paint
    const raf = requestAnimationFrame(computeScale);
    window.addEventListener('resize', computeScale);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', computeScale);
    };
  }, [open, computeScale]);

  if (!open) return null;

  const purchaseDate = new Date().toLocaleDateString('is-IS', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-[999] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
        <div>
          <h2 className="text-yellow-400 text-sm font-semibold tracking-wider uppercase"
              style={{ fontFamily: 'Georgia, serif' }}>
            Certificate of Authenticity
          </h2>
          <p className="text-white/40 text-xs mt-1 tracking-wide">
            Preview — this is emailed to the customer after purchase
          </p>
        </div>
        <button
          onClick={() => onOpenChange(false)}
          className="text-white/60 hover:text-white transition-colors p-2 -mr-2 rounded-lg hover:bg-white/10"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Certificate area */}
      <div
        ref={wrapRef}
        className="flex-1 flex items-center justify-center p-4 overflow-hidden"
      >
        {scale > 0 && (
          <div
            className="bg-white shadow-2xl shadow-white/5 rounded"
            style={{
              width: CERT_W * scale,
              height: CERT_H * scale,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: CERT_W,
                height: CERT_H,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <Certificate
                photo={photo}
                size={size}
                editionNumber={editionNumber}
                totalEditions={10}
                purchaseDate={purchaseDate}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="shrink-0 text-center py-3 border-t border-white/10">
        <p className="text-white/30 text-xs tracking-wide">
          Digital certificate — reduces paper usage &amp; carbon footprint
        </p>
      </div>
    </div>
  );
}
